import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import ReCAPTCHA from 'react-google-recaptcha';
import {
  Mic, Headphones, Film, Sparkles, Send, Paperclip, X,
  Mail, Phone, Instagram, ArrowRight, ArrowUpRight, CheckCircle2, Clock, Award, Radio,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_dubsync-3/artifacts/jj7xe7zi_Screenshot_3.jpeg.png';

const PROJECT_TYPES = [
  'Filme/Série', 'Animação', 'Comercial', 'Game',
  'Documentário', 'Audiobook', 'Locução', 'Voice-over', 'Outro',
];

const SERVICES = [
  {
    n: '01',
    icon: Film,
    title: 'Dublagem',
    desc: 'Filmes, séries, animações, jogos. Casting, direção e finalização entregues prontos para o ar.',
  },
  {
    n: '02',
    icon: Mic,
    title: 'Locução & Voice-over',
    desc: 'Comerciais, institucionais, narrações documentais e voz off corporativa com tom sob medida.',
  },
  {
    n: '03',
    icon: Headphones,
    title: 'Mixagem & Pós-produção',
    desc: 'Sincronização lábio-a-lábio, master M&E, sound design e entrega em vários formatos de áudio.',
  },
  {
    n: '04',
    icon: Sparkles,
    title: 'Casting & Direção',
    desc: 'Curadoria de elenco a partir do nosso banco de vozes e direção artística no estúdio.',
  },
];

const DIFFERENTIALS = [
  { icon: Award, title: 'Equipe selecionada', desc: 'Dubladores, mixadores e diretores experientes em múltiplos formatos.' },
  { icon: Clock, title: 'Prazos cumpridos', desc: 'Pipeline organizado com acompanhamento individual por projeto.' },
  { icon: CheckCircle2, title: 'Qualidade auditável', desc: 'Revisão técnica e artística antes de cada entrega.' },
];

/** Decorative concentric ring set — echoes the logo's mic grille pattern. */
const RingMotif = ({ className = '' }) => (
  <svg viewBox="0 0 400 400" className={className} aria-hidden="true">
    <defs>
      <radialGradient id="rmg" cx="50%" cy="50%" r="50%">
        <stop offset="0%"  stopColor="rgba(245,184,65,0.35)" />
        <stop offset="70%" stopColor="rgba(245,184,65,0)" />
      </radialGradient>
    </defs>
    <circle cx="200" cy="200" r="180" fill="url(#rmg)" />
    {[40, 65, 90, 115, 140, 165].map(r => (
      <circle key={r} cx="200" cy="200" r={r}
        fill="none" stroke="currentColor" strokeOpacity="0.18" strokeWidth="1" />
    ))}
    <circle cx="200" cy="200" r="12" fill="currentColor" fillOpacity="0.6" />
  </svg>
);

/** Waveform bars — subtle background motif. */
const Waveform = ({ className = '' }) => (
  <svg viewBox="0 0 600 80" className={className} aria-hidden="true">
    {Array.from({ length: 60 }).map((_, i) => {
      const h = 8 + Math.abs(Math.sin(i * 0.6) * 32) + Math.abs(Math.cos(i * 0.3) * 12);
      return <rect key={i} x={i * 10} y={40 - h / 2} width="3" height={h}
        rx="1.5" fill="currentColor" opacity={0.35 + (Math.sin(i * 0.4) * 0.35)} />;
    })}
  </svg>
);

const PublicLanding = () => {
  const [form, setForm] = useState({
    name: '', email: '', phone: '', company: '',
    project_type: '', estimated_duration: '', desired_deadline: '',
    estimated_budget: '', briefing: '',
  });
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);

  const handleChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 25 * 1024 * 1024) {
      toast.error('Arquivo maior que 25MB');
      e.target.value = '';
      return;
    }
    setFile(f);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.project_type || !form.briefing) {
      toast.error('Preencha os campos obrigatórios');
      return;
    }
    if (form.briefing.trim().length < 10) {
      toast.error('Conte um pouco mais sobre o projeto (mínimo 10 caracteres)');
      return;
    }
    if (RECAPTCHA_SITE_KEY && !captchaToken) {
      toast.error('Confirme o reCAPTCHA antes de enviar');
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (v) fd.append(k, v); });
      if (file) fd.append('file', file);
      if (captchaToken) fd.append('captcha_token', captchaToken);
      await axios.post(`${API}/budget-requests`, fd);
      setSubmitted(true);
      setForm({ name: '', email: '', phone: '', company: '', project_type: '',
        estimated_duration: '', desired_deadline: '', estimated_budget: '', briefing: '' });
      setFile(null);
      setCaptchaToken(null);
      recaptchaRef.current?.reset();
      toast.success('Solicitação enviada! Responderemos em breve.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao enviar. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white overflow-x-hidden dubl-landing" data-testid="public-landing">
      {/* Global grain + type styles */}
      <style>{`
        .dubl-landing {
          --brand:       #ffffff;
          --brand-ink:   #0a0a0a;
          --accent:      #f5b841;
          --accent-hot:  #ff9a1f;
          --line:        rgba(255,255,255,0.08);
          font-feature-settings: "ss01","cv11";
        }
        .dubl-landing .headline {
          letter-spacing: -0.04em;
          line-height: 0.92;
        }
        .dubl-landing .accent-underline {
          background-image: linear-gradient(180deg, transparent 65%, var(--accent) 65%, var(--accent) 92%, transparent 92%);
        }
        .dubl-landing .grain::before {
          content: "";
          position: absolute; inset: 0; pointer-events: none;
          background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.08 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>");
          mix-blend-mode: overlay; opacity: 0.4;
        }
        .dubl-landing .input-base {
          width: 100%;
          background: rgba(255,255,255,0.03);
          border: 1px solid var(--line);
          border-radius: 10px;
          padding: 0.75rem 0.9rem;
          color: #fff;
          font-size: 0.9rem;
          outline: none;
          transition: border-color 0.15s, background-color 0.15s;
        }
        .dubl-landing .input-base:focus { border-color: var(--accent); background: rgba(255,255,255,0.05); }
        .dubl-landing .input-base::placeholder { color: rgba(255,255,255,0.35); }
        .dubl-landing .btn-accent {
          background: var(--accent);
          color: var(--brand-ink);
          font-weight: 700;
          letter-spacing: 0.02em;
        }
        .dubl-landing .btn-accent:hover { background: var(--accent-hot); }
        .dubl-landing .live-dot {
          background: var(--accent);
          box-shadow: 0 0 0 4px rgba(245,184,65,0.15);
          animation: dubl-pulse 1.8s ease-in-out infinite;
        }
        @keyframes dubl-pulse {
          0%,100% { box-shadow: 0 0 0 4px rgba(245,184,65,0.15); }
          50%     { box-shadow: 0 0 0 10px rgba(245,184,65,0); }
        }
        .dubl-landing .marquee-track {
          display: inline-flex;
          animation: dubl-marquee 40s linear infinite;
          white-space: nowrap;
        }
        @keyframes dubl-marquee {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>

      {/* ================= NAVBAR ================= */}
      <nav className="sticky top-0 z-40 bg-[#0a0a0a]/90 backdrop-blur-md border-b border-white/5">
        <div className="w-full px-5 sm:px-8 lg:px-12 py-3 flex items-center justify-between gap-4">
          <a href="#hero" className="flex items-center gap-3 group" data-testid="nav-logo">
            <span className="inline-flex items-center justify-center h-9 w-9 rounded-lg bg-white text-black p-1">
              <img src={LOGO_URL} alt="DUBLEIROS" className="h-full w-full object-contain" />
            </span>
            <span className="font-black tracking-tight text-white text-lg leading-none">
              DUBLEIROS
              <span className="block text-[9px] text-white/50 tracking-[0.3em] font-normal mt-0.5">STUDIOS</span>
            </span>
          </a>
          <div className="hidden md:flex items-center gap-8 text-[13px]">
            <a href="#sobre"     className="text-white/60 hover:text-white transition-colors">Sobre</a>
            <a href="#servicos"  className="text-white/60 hover:text-white transition-colors">Serviços</a>
            <a href="#pipeline"  className="text-white/60 hover:text-white transition-colors">Pipeline</a>
            <a href="#orcamento" className="text-white/60 hover:text-white transition-colors">Orçamento</a>
          </div>
          <div className="flex items-center gap-2">
            <a
              href="#orcamento"
              className="hidden sm:inline-flex items-center gap-2 btn-accent px-4 py-2 rounded-lg text-sm transition-colors"
              data-testid="cta-nav-budget"
            >
              Solicitar Orçamento <ArrowRight className="h-3.5 w-3.5" />
            </a>
            <Link
              to="/equipe"
              className="text-[11px] text-white/50 hover:text-white transition-colors border border-white/10 hover:border-white/30 px-3 py-2 rounded-lg uppercase tracking-wider"
              data-testid="team-area-link"
            >
              Equipe
            </Link>
          </div>
        </div>
      </nav>

      {/* ================= HERO ================= */}
      <header id="hero" className="relative overflow-hidden">
        {/* Ring motif (right) */}
        <RingMotif className="absolute -right-24 -top-24 md:-right-16 w-[540px] md:w-[720px] text-white pointer-events-none" />
        {/* Grain overlay */}
        <div className="relative grain">
          <div className="w-full max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 pt-16 pb-24 md:pt-24 md:pb-32">
            {/* ON AIR badge */}
            <div className="inline-flex items-center gap-2 border border-white/10 rounded-full pl-2 pr-4 py-1.5 mb-8">
              <span className="live-dot h-2 w-2 rounded-full" />
              <span className="text-[11px] uppercase tracking-[0.32em] text-white/70 font-medium">On Air · Estúdio brasileiro</span>
            </div>

            {/* Big headline */}
            <h1 className="headline text-[clamp(2.6rem,7vw,6.25rem)] font-black text-white max-w-5xl">
              Vozes que dão <br className="hidden sm:block" />
              <span className="accent-underline">vida</span> ao seu projeto.
            </h1>

            <p className="mt-8 text-lg md:text-xl text-white/70 leading-relaxed max-w-2xl">
              Dublagem, locução e pós-produção de áudio para filmes, animações, comerciais, games e mais.
              Proposta personalizada em <span className="text-white font-semibold">até 48h</span>.
            </p>

            <div className="mt-10 flex flex-wrap gap-3">
              <a
                href="#orcamento"
                className="inline-flex items-center gap-2 btn-accent px-6 py-3.5 rounded-lg text-sm transition-colors"
                data-testid="cta-hero-budget"
              >
                Solicitar Orçamento
                <ArrowUpRight className="h-4 w-4" />
              </a>
              <a
                href="#servicos"
                className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 text-white px-6 py-3.5 rounded-lg text-sm font-medium border border-white/10 transition-colors"
              >
                Conhecer Serviços
              </a>
            </div>

            {/* Waveform decorative row */}
            <div className="mt-16 relative h-16 opacity-40">
              <Waveform className="absolute inset-0 w-full h-full text-white" />
            </div>

            {/* Differentials cards */}
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-4xl">
              {DIFFERENTIALS.map((d) => (
                <div key={d.title} className="bg-white/[0.03] hover:bg-white/[0.06] border border-white/10 rounded-2xl p-5 transition-colors">
                  <d.icon className="h-5 w-5 text-[color:var(--accent)] mb-3" />
                  <p className="text-sm font-bold text-white">{d.title}</p>
                  <p className="text-xs text-white/50 mt-1 leading-relaxed">{d.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Marquee strip */}
        <div className="relative overflow-hidden border-y border-white/5 bg-black/40 py-4">
          <div className="marquee-track text-white/40 font-black text-[clamp(1.75rem,4vw,3rem)] tracking-tight uppercase">
            {Array.from({ length: 2 }).map((_, i) => (
              <span key={i} className="flex items-center gap-8 pr-8">
                <span>Dublagem</span><span className="text-[color:var(--accent)]">✱</span>
                <span>Locução</span><span className="text-[color:var(--accent)]">✱</span>
                <span>Mixagem</span><span className="text-[color:var(--accent)]">✱</span>
                <span>Direção</span><span className="text-[color:var(--accent)]">✱</span>
                <span>Casting</span><span className="text-[color:var(--accent)]">✱</span>
                <span>Sound Design</span><span className="text-[color:var(--accent)]">✱</span>
              </span>
            ))}
          </div>
        </div>
      </header>

      {/* ================= SOBRE ================= */}
      <section id="sobre" className="relative">
        <div className="w-full max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-24 grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <p className="text-[10px] uppercase tracking-[0.4em] text-[color:var(--accent)] mb-4 font-mono">
              <span className="inline-block w-8 h-px bg-[color:var(--accent)] align-middle mr-2" />
              Sobre
            </p>
            <h2 className="headline text-4xl md:text-5xl font-black">Som que conta<br />uma história.</h2>
          </div>
          <div className="lg:col-span-7 space-y-5 text-white/70 text-base leading-relaxed">
            <p>
              O <span className="text-white font-semibold">DUBLEIROS STUDIOS</span> é um estúdio dedicado à dublagem brasileira,
              atendendo produtoras, distribuidoras, agências e creators que precisam levar sua obra ao público nacional
              com qualidade técnica e direção artística criteriosa.
            </p>
            <p className="text-white/50">
              Cuidamos de cada etapa — do casting de vozes, passando pela tradução adaptada,
              gravação, mixagem e entrega final nos formatos solicitados.
            </p>
            <div className="grid grid-cols-3 gap-6 pt-4 border-t border-white/10 mt-8">
              <div>
                <p className="headline text-4xl font-black text-[color:var(--accent)]">48h</p>
                <p className="text-xs text-white/50 mt-1">Prazo médio para proposta</p>
              </div>
              <div>
                <p className="headline text-4xl font-black text-white">100%</p>
                <p className="text-xs text-white/50 mt-1">Revisão técnica antes da entrega</p>
              </div>
              <div>
                <p className="headline text-4xl font-black text-white">+50</p>
                <p className="text-xs text-white/50 mt-1">Vozes disponíveis em elenco</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= SERVIÇOS ================= */}
      <section id="servicos" className="border-y border-white/5 bg-white/[0.02]">
        <div className="w-full max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-24">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
            <div>
              <p className="text-[10px] uppercase tracking-[0.4em] text-[color:var(--accent)] mb-4 font-mono">
                <span className="inline-block w-8 h-px bg-[color:var(--accent)] align-middle mr-2" />
                Serviços
              </p>
              <h2 className="headline text-4xl md:text-5xl font-black max-w-xl">O que entregamos.</h2>
            </div>
            <p className="text-white/50 text-sm max-w-sm">
              Pipeline completo de áudio — do briefing ao master. Escolha um serviço específico ou uma entrega chave-na-mão.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {SERVICES.map((s) => (
              <article
                key={s.title}
                className="group relative bg-black/40 border border-white/10 hover:border-[color:var(--accent)]/60 rounded-2xl p-6 transition-all duration-300"
              >
                <div className="flex items-start justify-between mb-8">
                  <s.icon className="h-8 w-8 text-white group-hover:text-[color:var(--accent)] transition-colors" />
                  <span className="text-[10px] font-mono text-white/30 tracking-wider">{s.n} / 04</span>
                </div>
                <h3 className="headline text-2xl font-black text-white mb-3">{s.title}</h3>
                <p className="text-sm text-white/50 leading-relaxed">{s.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ================= PIPELINE ================= */}
      <section id="pipeline" className="relative">
        <div className="w-full max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-24">
          <p className="text-[10px] uppercase tracking-[0.4em] text-[color:var(--accent)] mb-4 font-mono text-center">
            <span className="inline-block w-8 h-px bg-[color:var(--accent)] align-middle mr-2" />
            Pipeline
            <span className="inline-block w-8 h-px bg-[color:var(--accent)] align-middle ml-2" />
          </p>
          <h2 className="headline text-4xl md:text-5xl font-black text-center mb-16">Como trabalhamos.</h2>
          <div className="grid md:grid-cols-5 gap-3 relative">
            {[
              { n: '01', t: 'Briefing & Análise', d: 'Entendemos escopo, prazo e tom.' },
              { n: '02', t: 'Casting & Direção',  d: 'Vozes escolhidas com curadoria.' },
              { n: '03', t: 'Gravação',           d: 'No estúdio, com direção artística.' },
              { n: '04', t: 'Mixagem & Master',   d: 'Sync, M&E e afinação técnica.' },
              { n: '05', t: 'Entrega Final',      d: 'Nos formatos que você precisa.' },
            ].map((s, i, arr) => (
              <div
                key={s.n}
                className="relative bg-white/[0.03] border border-white/10 rounded-2xl p-5 hover:border-[color:var(--accent)]/60 transition-colors"
              >
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-[10px] font-mono text-[color:var(--accent)] tracking-wider">{s.n}</span>
                  <span className="h-px flex-1 bg-white/10" />
                  {i < arr.length - 1 && <ArrowRight className="h-3 w-3 text-white/30 hidden md:block" />}
                </div>
                <p className="font-bold text-white text-sm">{s.t}</p>
                <p className="text-xs text-white/40 mt-1 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= PORTFÓLIO ================= */}
      <section className="border-y border-white/5 bg-white/[0.02]">
        <div className="w-full max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-20 text-center">
          <Radio className="h-10 w-10 text-[color:var(--accent)] mx-auto mb-4" />
          <p className="text-[10px] uppercase tracking-[0.4em] text-[color:var(--accent)] mb-4 font-mono">Portfólio</p>
          <h2 className="headline text-4xl md:text-5xl font-black mb-6">Demos em breve.</h2>
          <p className="text-white/50 max-w-xl mx-auto text-base leading-relaxed">
            Estamos organizando uma seleção curada dos nossos trabalhos.
            Enquanto isso, peça uma <span className="accent-underline text-white">amostra personalizada</span> junto do seu orçamento —
            produzimos uma demo sob medida para o seu projeto.
          </p>
          <a
            href="#orcamento"
            className="mt-8 inline-flex items-center gap-2 text-[color:var(--accent)] hover:text-white transition-colors font-bold"
            data-testid="cta-portfolio-demo"
          >
            Pedir demo personalizada <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>
      </section>

      {/* ================= ORÇAMENTO ================= */}
      <section id="orcamento" className="relative">
        <div className="w-full max-w-4xl mx-auto px-5 sm:px-8 py-24">
          <p className="text-[10px] uppercase tracking-[0.4em] text-[color:var(--accent)] mb-4 font-mono">
            <span className="inline-block w-8 h-px bg-[color:var(--accent)] align-middle mr-2" />
            Orçamento
          </p>
          <h2 className="headline text-4xl md:text-5xl font-black mb-4">Conte sobre o seu projeto.</h2>
          <p className="text-white/50 mb-12 text-base">Preencha os dados abaixo. Respondemos em até 48h úteis.</p>

          {submitted ? (
            <div className="bg-[color:var(--accent)]/10 border-2 border-[color:var(--accent)]/40 rounded-2xl p-10 text-center" data-testid="budget-submitted">
              <CheckCircle2 className="h-14 w-14 text-[color:var(--accent)] mx-auto mb-4" />
              <h3 className="headline text-2xl font-black text-white mb-2">Recebemos sua solicitação!</h3>
              <p className="text-white/70 mb-6">Em até 48h úteis você recebe nossa proposta no email informado.</p>
              <button
                onClick={() => setSubmitted(false)}
                className="text-[color:var(--accent)] hover:text-white text-sm font-bold transition-colors"
                data-testid="budget-new-request"
              >
                Enviar outra solicitação →
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-white/[0.03] border border-white/10 rounded-2xl p-6 sm:p-8 space-y-5" data-testid="budget-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Nome *">
                  <input required value={form.name} onChange={handleChange('name')}
                    className="input-base" placeholder="Seu nome completo" data-testid="budget-name" />
                </Field>
                <Field label="Email *">
                  <input required type="email" value={form.email} onChange={handleChange('email')}
                    className="input-base" placeholder="voce@email.com" data-testid="budget-email" />
                </Field>
                <Field label="Telefone">
                  <input value={form.phone} onChange={handleChange('phone')}
                    className="input-base" placeholder="(11) 99999-9999" data-testid="budget-phone" />
                </Field>
                <Field label="Empresa">
                  <input value={form.company} onChange={handleChange('company')}
                    className="input-base" placeholder="Opcional" data-testid="budget-company" />
                </Field>
                <Field label="Tipo de Projeto *">
                  <select required value={form.project_type} onChange={handleChange('project_type')}
                    className="input-base" data-testid="budget-project-type">
                    <option value="">— Selecione —</option>
                    {PROJECT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Field>
                <Field label="Duração estimada">
                  <input value={form.estimated_duration} onChange={handleChange('estimated_duration')}
                    className="input-base" placeholder="Ex: 30s, 5min, 10 episódios" data-testid="budget-duration" />
                </Field>
                <Field label="Prazo desejado">
                  <input value={form.desired_deadline} onChange={handleChange('desired_deadline')}
                    className="input-base" placeholder="Ex: até 15/04, sem urgência" data-testid="budget-deadline" />
                </Field>
                <Field label="Orçamento estimado">
                  <input value={form.estimated_budget} onChange={handleChange('estimated_budget')}
                    className="input-base" placeholder="Opcional — Ex: R$ 5.000" data-testid="budget-amount" />
                </Field>
              </div>

              <Field label="Briefing / Detalhes do projeto *">
                <textarea required value={form.briefing} onChange={handleChange('briefing')}
                  rows={5} className="input-base resize-none"
                  placeholder="Conte sobre o conteúdo, idioma original, vozes desejadas, formato de entrega, etc."
                  data-testid="budget-briefing" />
              </Field>

              <div>
                <label className="block text-sm font-medium text-white/70 mb-1.5">Arquivo de referência (opcional, até 25MB)</label>
                {file ? (
                  <div className="flex items-center justify-between bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2.5" data-testid="budget-file-attached">
                    <span className="flex items-center gap-2 text-sm text-white truncate">
                      <Paperclip className="h-4 w-4 text-[color:var(--accent)]" />
                      {file.name}
                    </span>
                    <button type="button" onClick={() => setFile(null)} className="text-white/40 hover:text-red-400">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <label className="flex items-center gap-2 bg-white/[0.02] hover:bg-white/[0.05] border border-dashed border-white/15 rounded-lg px-3 py-3 cursor-pointer text-sm text-white/60 transition-colors">
                    <Paperclip className="h-4 w-4 text-white/40" />
                    <span>Anexar vídeo, áudio, script ou imagem</span>
                    <input type="file" onChange={handleFile} className="hidden"
                      accept="video/*,audio/*,image/*,.pdf,.zip,.txt,.doc,.docx"
                      data-testid="budget-file-input" />
                  </label>
                )}
              </div>

              {RECAPTCHA_SITE_KEY && (
                <div className="flex justify-center pt-1" data-testid="recaptcha-wrapper">
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={RECAPTCHA_SITE_KEY}
                    theme="dark"
                    onChange={(token) => setCaptchaToken(token)}
                    onExpired={() => setCaptchaToken(null)}
                  />
                </div>
              )}

              <button
                type="submit"
                disabled={submitting || (RECAPTCHA_SITE_KEY && !captchaToken)}
                className="w-full inline-flex items-center justify-center gap-2 btn-accent disabled:bg-white/10 disabled:text-white/30 disabled:cursor-not-allowed py-4 rounded-lg text-sm transition-colors uppercase tracking-wider"
                data-testid="budget-submit"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-black"></div>
                    Enviando...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    Enviar Solicitação
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* ================= FOOTER ================= */}
      <footer className="border-t border-white/5 bg-black">
        <div className="w-full max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-14 grid md:grid-cols-3 gap-10">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="inline-flex items-center justify-center h-10 w-10 rounded-lg bg-white p-1">
                <img src={LOGO_URL} alt="DUBLEIROS" className="h-full w-full object-contain" />
              </span>
              <div>
                <p className="font-black tracking-tight text-white text-base leading-none">DUBLEIROS</p>
                <p className="text-[9px] text-white/40 tracking-[0.3em] mt-1">STUDIOS</p>
              </div>
            </div>
            <p className="text-sm text-white/50 leading-relaxed">
              Estúdio brasileiro de dublagem, locução e pós-produção de áudio.
            </p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-4 font-mono">Contato</p>
            <ul className="space-y-3 text-sm text-white/70">
              <li className="flex items-center gap-2"><Mail className="h-4 w-4 text-[color:var(--accent)]" /> contato@dubleiros.com</li>
              <li className="flex items-center gap-2"><Phone className="h-4 w-4 text-[color:var(--accent)]" /> Em breve</li>
              <li className="flex items-center gap-2"><Instagram className="h-4 w-4 text-[color:var(--accent)]" /> @dubleiros</li>
            </ul>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-4 font-mono">Navegar</p>
            <ul className="space-y-3 text-sm text-white/70">
              <li><a href="#servicos"  className="hover:text-[color:var(--accent)] transition-colors">Serviços</a></li>
              <li><a href="#pipeline"  className="hover:text-[color:var(--accent)] transition-colors">Pipeline</a></li>
              <li><a href="#orcamento" className="hover:text-[color:var(--accent)] transition-colors">Solicitar Orçamento</a></li>
              <li>
                <Link to="/equipe" className="hover:text-[color:var(--accent)] transition-colors" data-testid="footer-team-link">
                  Área da Equipe
                </Link>
              </li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/5 py-5 text-center text-[11px] text-white/30 tracking-wider">
          © {new Date().getFullYear()} DUBLEIROS STUDIOS · Todos os direitos reservados.
        </div>
      </footer>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="block text-sm font-medium text-white/70 mb-1.5">{label}</label>
    {children}
  </div>
);

export default PublicLanding;
